import React, { useEffect, useMemo, useState } from 'react';
import { ProductConsolidationAPI } from '../services/productConsolidation';
import { getErrorMessage, isUnauthorizedError } from '../services/http';
import OperationStatus from './OperationStatus';

const PAGE_SIZE = 20;
const COMPARISON_FIELDS = [
  ['name', 'Nome'],
  ['description', 'Descrição'],
  ['code', 'Código'],
  ['sku', 'SKU'],
  ['barcode', 'Código de barras'],
  ['unit', 'Unidade'],
  ['category', 'Categoria'],
  ['brand', 'Marca'],
  ['type', 'Tipo'],
  ['price', 'Preço'],
  ['cost', 'Custo'],
  ['stock', 'Estoque']
];

function formatValue(value, fallback = 'Não informado') {
  return value === null || value === undefined || String(value).trim() === '' ? fallback : String(value);
}

function formatSource(source) {
  return ({ bling: 'Bling', auvo: 'Auvo', cec_integrated: 'Produtos integrados', manual: 'Manual', master: 'Produto Mestre' }[source] || formatValue(source, 'Origem'));
}

function emptySearchResults() {
  return { masterProducts: [], blingProducts: [], auvoProducts: [], linkedRecords: [] };
}

function externalKey(record) {
  return `${record.sourceSystem}:${record.importedProductId || record.id}:${record.externalId || ''}`;
}

export default function ProductConsolidationPanel({ pageMode = false, onClose, onUnauthorized }) {
  const [summary, setSummary] = useState({ dashboard: null });
  const [mastersPayload, setMastersPayload] = useState({ rows: [], page: 1, totalPages: 1, total: 0 });
  const [linksPayload, setLinksPayload] = useState({ rows: [], page: 1, totalPages: 1, total: 0 });
  const [activeTab, setActiveTab] = useState('masters');
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState('consolidate');
  const [filters, setFilters] = useState({ q: '', origin: '' });
  const [pages, setPages] = useState({ masters: 1, links: 1 });
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [showLoadStatus, setShowLoadStatus] = useState(false);
  const [actionId, setActionId] = useState('');
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [selectedAuditKeys, setSelectedAuditKeys] = useState([]);
  const [dataComparison, setDataComparison] = useState(null);
  const [comparisonUpdates, setComparisonUpdates] = useState({});
  const [unifiedQuery, setUnifiedQuery] = useState('');
  const [unifiedResults, setUnifiedResults] = useState(() => emptySearchResults());
  const [selectedMasterId, setSelectedMasterId] = useState('');
  const [selectedExternalKeys, setSelectedExternalKeys] = useState([]);
  const [mergeSourceMaster, setMergeSourceMaster] = useState(null);

  const dashboard = summary.dashboard || {};
  const sourceDistribution = dashboard.sourceDistribution || {};
  const consolidation = dashboard.consolidation || {};
  const dataQuality = dashboard.dataQuality || {};
  const sourceHealth = dashboard.sourceHealth || {};
  const sourceCoverage = dashboard.sourceCoverage || {};
  const allExternalResults = useMemo(() => [...(unifiedResults.blingProducts || []), ...(unifiedResults.auvoProducts || [])], [unifiedResults]);
  const selectedExternalRecords = useMemo(() => allExternalResults.filter((record) => selectedExternalKeys.includes(externalKey(record))), [allExternalResults, selectedExternalKeys]);

  const handleApiError = (requestError, fallbackMessage) => {
    if (isUnauthorizedError(requestError)) {
      onUnauthorized?.();
      return;
    }
    setError(getErrorMessage(requestError, fallbackMessage));
  };

  const tableParams = (tab, page = pages[tab]) => ({ q: filters.q.trim(), origin: filters.origin, page, limit: PAGE_SIZE });
  const loadDashboard = async () => setSummary((await ProductConsolidationAPI.suggestions()).summary || {});
  const loadMasters = async (page = pages.masters) => setMastersPayload(await ProductConsolidationAPI.masters(tableParams('masters', page)));
  const loadLinks = async (page = pages.links) => setLinksPayload(await ProductConsolidationAPI.links(tableParams('links', page)));

  const loadAll = async ({ showStatus = false } = {}) => {
    setLoading(true);
    setTableLoading(true);
    setShowLoadStatus(showStatus);
    setError('');
    try {
      await Promise.all([loadDashboard(), loadMasters(1), loadLinks(1)]);
      setPages({ masters: 1, links: 1 });
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível carregar a consolidação de produtos.');
    } finally {
      setLoading(false);
      setTableLoading(false);
      setShowLoadStatus(false);
    }
  };

  const handleApplyFilters = async (event) => {
    event.preventDefault();
    setPages({ masters: 1, links: 1 });
    setTableLoading(true);
    try {
      if (activeTab === 'masters') await loadMasters(1);
      if (activeTab === 'links') await loadLinks(1);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível aplicar os filtros.');
    } finally {
      setTableLoading(false);
    }
  };

  const openMasterDetail = async (masterProductId) => {
    if (!masterProductId) return;
    setActionId('open-detail');
    setError('');
    try {
      const payload = await ProductConsolidationAPI.auditCandidates(masterProductId);
      setDetail(payload);
      setSelectedAuditKeys([]);
      setDataComparison(null);
      setComparisonUpdates({});
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível abrir o Produto Mestre.');
    } finally {
      setActionId('');
    }
  };

  const runUnifiedSearch = async (event) => {
    event?.preventDefault();
    if (!unifiedQuery.trim()) return;
    setActionId('unified-search');
    setError('');
    try {
      const payload = await ProductConsolidationAPI.search(unifiedQuery);
      setUnifiedResults(payload || emptySearchResults());
      setSelectedMasterId(payload?.masterProducts?.[0]?.id || payload?.masterProducts?.[0]?._id || '');
      setSelectedExternalKeys([]);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível buscar produtos em Produto Mestre, Bling e Auvo.');
    } finally {
      setActionId('');
    }
  };

  const toggleExternalRecord = (record) => {
    const key = externalKey(record);
    setSelectedExternalKeys((current) => (current.includes(key) ? current.filter((value) => value !== key) : [...current, key]));
  };

  const externalPayload = (records = selectedExternalRecords) => records.map((record) => ({
    sourceSystem: record.sourceSystem,
    externalId: record.externalId,
    importedProductId: record.importedProductId || record.id
  }));

  const handleLinkSelected = async () => {
    if (!selectedMasterId || !selectedExternalRecords.length) return;
    setActionId('link-selected');
    setError('');
    try {
      await ProductConsolidationAPI.linkSelected({ masterProductId: selectedMasterId, externalRecords: externalPayload() });
      await Promise.all([runUnifiedSearch(), loadAll()]);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível vincular os produtos selecionados.');
    } finally {
      setActionId('');
    }
  };

  const handleCreateMasterWithSelected = async () => {
    if (!selectedExternalRecords.length) return;
    setActionId('create-master-selected');
    setError('');
    try {
      await ProductConsolidationAPI.createMasterWithSelected({ externalRecords: externalPayload() });
      await Promise.all([runUnifiedSearch(), loadAll()]);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível criar Produto Mestre com os selecionados.');
    } finally {
      setActionId('');
    }
  };

  const auditKey = (candidate) => `${candidate.sourceSystem}:${candidate.importedProductId}:${candidate.externalId || ''}`;
  const selectedAuditCandidates = () => [...(detail?.blingCandidates || []), ...(detail?.auvoCandidates || [])].filter((candidate) => selectedAuditKeys.includes(auditKey(candidate)));

  const handleLinkAuditCandidates = async () => {
    if (!detail?.masterProduct?._id) return;
    const selected = selectedAuditCandidates();
    if (!selected.length) return;
    setActionId('link-audit');
    try {
      await ProductConsolidationAPI.linkCandidates(detail.masterProduct._id, externalPayload(selected));
      await openMasterDetail(detail.masterProduct._id);
      await loadAll();
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível vincular os candidatos.');
    } finally {
      setActionId('');
    }
  };

  const handleOpenDataComparison = async () => {
    if (!detail?.masterProduct?._id) return;
    setActionId('data-comparison');
    setError('');
    try {
      const payload = await ProductConsolidationAPI.dataComparison(detail.masterProduct._id);
      const suggested = {};
      for (const item of payload.suggestedUpdates || []) suggested[item.field] = item.suggestedValue;
      setDataComparison(payload);
      setComparisonUpdates(suggested);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível abrir a comparação de dados.');
    } finally {
      setActionId('');
    }
  };

  const handleApplyDataComparison = async () => {
    if (!detail?.masterProduct?._id) return;
    const updates = Object.fromEntries(Object.entries(comparisonUpdates).filter(([, value]) => String(value || '').trim()));
    setActionId('apply-comparison');
    try {
      const payload = await ProductConsolidationAPI.applyDataCompletion(detail.masterProduct._id, updates);
      const refreshed = await ProductConsolidationAPI.dataComparison(detail.masterProduct._id);
      setDetail((current) => current ? { ...current, masterProduct: payload.masterProduct || current.masterProduct } : current);
      setDataComparison(refreshed);
      setComparisonUpdates({});
      await loadDashboard();
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível completar os dados do Produto Mestre.');
    } finally {
      setActionId('');
    }
  };

  const handleMergeMasterInto = async (targetMaster) => {
    if (!mergeSourceMaster?.id || !targetMaster?.id || mergeSourceMaster.id === targetMaster.id) return;
    const confirmed = window.confirm(`Mesclar "${mergeSourceMaster.name}" em "${targetMaster.name}"? O produto mestre de origem ficará inativo e seus vínculos serão movidos para o destino.`);
    if (!confirmed) return;

    setActionId(`merge-${mergeSourceMaster.id}-${targetMaster.id}`);
    setError('');
    try {
      await ProductConsolidationAPI.mergeMasters({
        sourceMasterProductId: mergeSourceMaster.id,
        targetMasterProductId: targetMaster.id
      });
      setMergeSourceMaster(null);
      await Promise.all([loadMasters(pages.masters), loadLinks(pages.links), loadDashboard()]);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível mesclar os Produtos Mestres. Confira se código, SKU ou barras são compatíveis.');
    } finally {
      setActionId('');
    }
  };

  const handleMasterMergeButton = (event, master) => {
    event.stopPropagation();
    if (!mergeSourceMaster) {
      setMergeSourceMaster(master);
      return;
    }
    if (mergeSourceMaster.id === master.id) {
      setMergeSourceMaster(null);
      return;
    }
    void handleMergeMasterInto(master);
  };

  useEffect(() => {
    if (activeWorkspaceTab !== 'records') return;
    setTableLoading(true);
    setError('');
    const loader = activeTab === 'masters' ? loadMasters : loadLinks;
    loader(pages[activeTab])
      .catch((requestError) => handleApiError(requestError, 'Não foi possível carregar os dados da aba.'))
      .finally(() => setTableLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeWorkspaceTab]);

  useEffect(() => {
    if (activeWorkspaceTab !== 'overview') return;
    setLoading(true);
    setError('');
    loadDashboard()
      .catch((requestError) => handleApiError(requestError, 'Não foi possível carregar os indicadores de produtos.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspaceTab]);

  const openRecordsWorkspace = () => {
    setActiveWorkspaceTab('records');
  };

  const openOverviewWorkspace = () => {
    setActiveWorkspaceTab('overview');
  };

  const renderPager = (tab, payload) => (
    <div className="client-table-pager">
      <span>Página {payload.page || 1} de {payload.totalPages || 1} · {payload.total || 0} registros</span>
      <div className="overlay-actions">
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={(payload.page || 1) <= 1 || tableLoading} onClick={() => { const next = (pages[tab] || 1) - 1; setPages((current) => ({ ...current, [tab]: next })); tab === 'masters' ? loadMasters(next) : loadLinks(next); }}>Anterior</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={(payload.page || 1) >= (payload.totalPages || 1) || tableLoading} onClick={() => { const next = (pages[tab] || 1) + 1; setPages((current) => ({ ...current, [tab]: next })); tab === 'masters' ? loadMasters(next) : loadLinks(next); }}>Próxima</button>
      </div>
    </div>
  );

  const renderUnifiedRecord = (record, type) => {
    const isMaster = type === 'master';
    const checked = isMaster ? selectedMasterId === (record.id || record._id) : selectedExternalKeys.includes(externalKey(record));
    return (
      <label className="client-unified-result" key={isMaster ? record.id || record._id : externalKey(record)}>
        <input type={isMaster ? 'radio' : 'checkbox'} name="selectedMasterProduct" checked={checked} onChange={() => (isMaster ? setSelectedMasterId(record.id || record._id) : toggleExternalRecord(record))} />
        <span>
          <strong>{formatValue(record.name)}</strong>
          <small>{formatValue(record.code || record.sku, 'Sem código')} · {formatValue(record.barcode, 'Sem barras')} · {formatValue(record.unit, 'Sem unidade')}</small>
        </span>
      </label>
    );
  };

  const firstSourceValue = (records, field) => records?.map((record) => record[field]).find((value) => value !== undefined && value !== null && String(value).trim() !== '') || '';

  const renderDataComparison = () => {
    if (!dataComparison) return null;
    return (
      <div className="client-detail-section mt-3">
        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
          <h3>Comparativo para completar dados</h3>
          <button type="button" className="btn btn-sm btn-primary" disabled={actionId === 'apply-comparison'} onClick={handleApplyDataComparison}>Aplicar valores escolhidos</button>
        </div>
        <table className="client-audit-table client-audit-table--compact">
          <thead><tr><th>Campo</th><th>Produto Mestre</th><th>Bling</th><th>Auvo</th><th>Valor escolhido</th></tr></thead>
          <tbody>
            {COMPARISON_FIELDS.map(([field, label]) => {
              const masterValue = dataComparison.masterProduct?.[field] ?? '';
              const blingValue = firstSourceValue(dataComparison.blingRecords, field);
              const auvoValue = firstSourceValue(dataComparison.auvoRecords, field);
              const options = [...new Set([masterValue, blingValue, auvoValue].filter((value) => value !== undefined && value !== null && String(value).trim() !== ''))];
              return (
                <tr key={field}>
                  <td>{label}</td>
                  <td>{formatValue(masterValue, 'vazio')}</td>
                  <td>{formatValue(blingValue, 'vazio')}</td>
                  <td>{formatValue(auvoValue, 'vazio')}</td>
                  <td>
                    <select className="form-select" value={comparisonUpdates[field] || ''} onChange={(event) => setComparisonUpdates((current) => ({ ...current, [field]: event.target.value }))}>
                      <option value="">Manter atual</option>
                      {options.map((value) => <option key={`${field}-${value}`} value={value}>{value}</option>)}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCandidateTable = (title, rows) => (
    <div className="client-detail-section mt-3">
      <h3>{title}</h3>
      <table className="client-audit-table client-audit-table--compact">
        <thead><tr><th></th><th>Nome</th><th>Código</th><th>SKU</th><th>Barras</th><th>Unidade</th><th>Preço</th><th>Status</th><th>Score</th><th>Motivos</th></tr></thead>
        <tbody>
          {!rows.length && <tr><td colSpan="10">Nenhum candidato encontrado.</td></tr>}
          {rows.map((candidate) => {
            const key = auditKey(candidate);
            return (
              <tr key={key}>
                <td><input type="checkbox" checked={selectedAuditKeys.includes(key)} disabled={candidate.alreadyLinked} onChange={() => setSelectedAuditKeys((current) => current.includes(key) ? current.filter((value) => value !== key) : [...current, key])} /></td>
                <td>{formatValue(candidate.name)}</td>
                <td>{formatValue(candidate.code)}</td>
                <td>{formatValue(candidate.sku)}</td>
                <td>{formatValue(candidate.barcode)}</td>
                <td>{formatValue(candidate.unit)}</td>
                <td>{formatValue(candidate.price)}</td>
                <td>{candidate.alreadyLinked ? 'Vinculado' : 'Possível vínculo'}</td>
                <td>{candidate.confidenceScore || 0}%</td>
                <td>{candidate.matchReasons?.join(', ') || 'Sem motivo'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const ShellTag = pageMode ? 'main' : 'section';
  const operationTitle = actionId === 'unified-search'
    ? 'Buscando produtos no TaskHub, Bling e AUVO'
    : loading && showLoadStatus
      ? 'Atualizando consolidação de produtos'
      : '';

  return (
    <ShellTag className={pageMode ? 'client-consolidation-page' : 'client-consolidation-panel'}>
      <div className="overlay-panel-header">
        <div>
          <div className="eyebrow">Cadastro Mestre</div>
          <h2>Consolidação de Produtos</h2>
          <p className="text-muted mb-0">Audite vínculos entre produtos importados e Produtos Mestres do Task Hub.</p>
        </div>
        {pageMode && <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Voltar</button>}
      </div>

      <div className="overlay-actions mt-3">
        <button type="button" className="btn btn-success" onClick={() => { window.location.href = '/sync-center'; }} disabled={loading}>Abrir Central de Sincronização</button>
        <button type="button" className="btn btn-outline-primary" onClick={() => loadAll({ showStatus: true })} disabled={loading}>Atualizar auditoria</button>
      </div>

      <OperationStatus
        active={Boolean(operationTitle)}
        title={operationTitle}
        steps={[
          actionId === 'unified-search' && 'Consultando cadastro mestre e dados integrados.',
          loading && showLoadStatus && 'Sincronizando com o cec_api_integra quando necessário.',
          loading && showLoadStatus && 'Montando listas e totais para exibição.',
        ]}
      />

      {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}

      <div className="service-workspace-tabs mt-3" role="tablist" aria-label="Área de produtos">
        <button type="button" className={activeWorkspaceTab === 'consolidate' ? 'active' : ''} onClick={() => setActiveWorkspaceTab('consolidate')}>
          Consolidar produtos
        </button>
        <button type="button" className={activeWorkspaceTab === 'records' ? 'active' : ''} onClick={openRecordsWorkspace}>
          Produtos e vínculos
        </button>
        <button type="button" className={activeWorkspaceTab === 'overview' ? 'active' : ''} onClick={openOverviewWorkspace}>
          Indicadores
        </button>
      </div>

      {activeWorkspaceTab === 'overview' && (
      <section className="service-workspace-panel">
        <div className="service-workspace-heading mt-3">
          <div>
            <div className="eyebrow">Indicadores</div>
            <h3>Visão de produtos</h3>
          </div>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => loadAll({ showStatus: true })} disabled={loading}>
            Atualizar
          </button>
        </div>
        <div className="client-dashboard">
          <div className="client-total-card"><span>Produtos Totais Encontrados</span><strong>{dashboard.totalProducts || 0}</strong><small>produtos</small></div>
          <div className="client-dashboard-section">
            <h3>Distribuição por origem</h3>
            <p><span>Bling</span><strong>{sourceDistribution.bling || 0}</strong></p>
            <p><span>Auvo</span><strong>{sourceDistribution.auvo || 0}</strong></p>
            <p><span>Produtos integrados</span><strong>{sourceDistribution.cecIntegrated || 0}</strong></p>
            <p><span>Manual</span><strong>{sourceDistribution.manual || 0}</strong></p>
          </div>
          <div className="client-dashboard-section">
            <h3>Situação da Consolidação</h3>
            <p><span>Integrados ao Produto Mestre</span><strong>{consolidation.integrated || 0}</strong></p>
            <p><span>Pendentes</span><strong>{consolidation.pending || 0}</strong></p>
            <p><span>Ignorados</span><strong>{consolidation.ignored || 0}</strong></p>
            <p><span>Sugestões</span><strong>{consolidation.duplicateSuggestions || 0}</strong></p>
          </div>
          <div className="client-dashboard-section">
            <h3>Qualidade dos dados</h3>
            <p><span>Com código/SKU</span><strong>{dataQuality.withCode || 0}</strong></p>
            <p><span>Com código de barras</span><strong>{dataQuality.withBarcode || 0}</strong></p>
            <p><span>Com preço</span><strong>{dataQuality.withPrice || 0}</strong></p>
            <p><span>Completos</span><strong>{dataQuality.completeRecords || 0}</strong></p>
          </div>
          <div className="client-dashboard-section client-dashboard-section--attention">
            <h3>Saneamento da integração</h3>
            <p><span>Bling sem Auvo</span><strong>{sourceCoverage.onlyBling || 0}</strong></p>
            <p><span>Auvo sem Bling</span><strong>{sourceCoverage.onlyAuvo || 0}</strong></p>
            <p><span>Status divergente</span><strong>{sourceCoverage.statusDivergences || 0}</strong></p>
            <p><span>Inativos nas origens</span><strong>{sourceHealth.inactive || 0}</strong></p>
            <p><span>Removidos nas origens</span><strong>{sourceHealth.removed || 0}</strong></p>
          </div>
        </div>
      </section>
      )}

      {activeWorkspaceTab === 'records' && (
      <section className="service-workspace-panel">
      <div className="service-workspace-heading mt-3">
        <div>
          <div className="eyebrow">Cadastros</div>
          <h3>Produtos consolidados e vínculos</h3>
        </div>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => loadAll({ showStatus: true })} disabled={loading}>
          Atualizar
        </button>
      </div>
      <form className="client-filter-bar mt-4" onSubmit={handleApplyFilters}>
        <div className="filter-control"><label className="form-label">Busca</label><input className="form-control" value={filters.q} onChange={(event) => setFilters((current) => ({ ...current, q: event.target.value }))} placeholder="Nome, código, SKU, barras ou categoria" /></div>
        <div className="filter-control"><label className="form-label">Origem</label><select className="form-select" value={filters.origin} onChange={(event) => setFilters((current) => ({ ...current, origin: event.target.value }))}><option value="">Todas</option><option value="bling">Bling</option><option value="auvo">Auvo</option><option value="cec_integrated">Integrados</option><option value="manual">Manual</option></select></div>
        <button type="submit" className="btn btn-dark" disabled={tableLoading}>Aplicar filtros</button>
      </form>

      <div className="task-tabs mt-4">
        <button type="button" className={`task-tab ${activeTab === 'masters' ? 'task-tab--active' : ''}`} onClick={() => setActiveTab('masters')}>Produtos Consolidados</button>
        <button type="button" className={`task-tab ${activeTab === 'links' ? 'task-tab--active' : ''}`} onClick={() => setActiveTab('links')}>Vínculos</button>
      </div>

      {activeTab === 'masters' && (
        <div className="client-table-wrap mt-3">
          {mergeSourceMaster && (
            <div className="alert alert-warning d-flex justify-content-between align-items-center gap-3 flex-wrap mb-3">
              <div>
                <strong>{formatValue(mergeSourceMaster.name)}</strong> será mesclado no Produto Mestre escolhido como destino.
                <div className="small">Clique em “Mesclar aqui” na linha que deve permanecer.</div>
              </div>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setMergeSourceMaster(null)}>
                Cancelar mesclagem
              </button>
            </div>
          )}
          {renderPager('masters', mastersPayload)}
          <table className="client-audit-table"><thead><tr><th>Produto Mestre</th><th>Código</th><th>SKU</th><th>Barras</th><th>Unidade</th><th>Categoria</th><th>Vínculos</th><th>Origens</th><th>Ações</th></tr></thead><tbody>
            {tableLoading && <tr><td colSpan="9">Carregando produtos...</td></tr>}
            {!tableLoading && !mastersPayload.rows?.length && <tr><td colSpan="9">Nenhum Produto Mestre encontrado.</td></tr>}
            {!tableLoading && mastersPayload.rows?.map((master) => (
              <tr key={master.id} onClick={() => openMasterDetail(master.id)}>
                <td><strong>{formatValue(master.name)}</strong><small>{formatValue(master.description, '')}</small></td>
                <td>{formatValue(master.code)}</td>
                <td>{formatValue(master.sku)}</td>
                <td>{formatValue(master.barcode)}</td>
                <td>{formatValue(master.unit)}</td>
                <td>{formatValue(master.category)}</td>
                <td>{master.linkCount || 0}</td>
                <td>{master.linkedOrigins?.map(formatSource).join(', ') || 'Sem vínculos'}</td>
                <td>
                  <button
                    type="button"
                    className={`btn btn-sm ${mergeSourceMaster?.id && mergeSourceMaster.id !== master.id ? 'btn-primary' : 'btn-outline-primary'}`}
                    disabled={Boolean(actionId)}
                    onClick={(event) => handleMasterMergeButton(event, master)}
                  >
                    {mergeSourceMaster?.id === master.id ? 'Cancelar' : mergeSourceMaster?.id ? 'Mesclar aqui' : 'Mesclar em outro'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody></table>
          {renderPager('masters', mastersPayload)}
        </div>
      )}

      {activeTab === 'links' && (
        <div className="client-table-wrap mt-3">
          {renderPager('links', linksPayload)}
          <table className="client-audit-table"><thead><tr><th>Produto Mestre</th><th>Origem</th><th>ID externo</th><th>Nome original</th><th>Código</th><th>SKU</th><th>Barras</th><th>Confiança</th></tr></thead><tbody>
            {tableLoading && <tr><td colSpan="8">Carregando vínculos...</td></tr>}
            {!tableLoading && !linksPayload.rows?.length && <tr><td colSpan="8">Nenhum vínculo encontrado.</td></tr>}
            {!tableLoading && linksPayload.rows?.map((link) => <tr key={link.id} onClick={() => openMasterDetail(link.masterProduct?._id)}><td>{formatValue(link.masterProduct?.name)}</td><td>{formatSource(link.sourceSystem)}</td><td>{formatValue(link.externalId)}</td><td>{formatValue(link.externalName)}</td><td>{formatValue(link.code)}</td><td>{formatValue(link.sku)}</td><td>{formatValue(link.barcode)}</td><td>{link.confidenceScore || 0}%</td></tr>)}
          </tbody></table>
          {renderPager('links', linksPayload)}
        </div>
      )}
      </section>
      )}

      {activeWorkspaceTab === 'consolidate' && (
      <section className="client-unified-search mt-4">
        <form className="client-unified-search__bar" onSubmit={runUnifiedSearch}>
          <div><div className="eyebrow">Busca unificada</div><h3>Pesquisar Produto Mestre, Bling e Auvo</h3></div>
          <input className="form-control" value={unifiedQuery} onChange={(event) => setUnifiedQuery(event.target.value)} placeholder="Ex.: filtro, compressor, gás" />
          <button type="submit" className="btn btn-dark" disabled={actionId === 'unified-search'}>Buscar</button>
        </form>
        <OperationStatus
          active={actionId === 'unified-search'}
          title="Busca unificada em andamento"
          steps={['Pesquisando Produto Mestre.', 'Consultando dados consolidados do Bling e AUVO.', 'Preparando resultados para seleção.']}
        />
        {!!unifiedQuery.trim() && (
          <div className="client-unified-grid mt-3">
            <div className="client-unified-block"><h3>Produto Mestre</h3>{!unifiedResults.masterProducts?.length && <div className="empty-state">Nenhum Produto Mestre encontrado.</div>}{unifiedResults.masterProducts?.map((record) => renderUnifiedRecord(record, 'master'))}</div>
            <div className="client-unified-block"><h3>Produtos Bling</h3>{!unifiedResults.blingProducts?.length && <div className="empty-state">Nenhum produto Bling encontrado.</div>}{unifiedResults.blingProducts?.map((record) => renderUnifiedRecord(record, 'external'))}</div>
            <div className="client-unified-block"><h3>Produtos Auvo</h3>{!unifiedResults.auvoProducts?.length && <div className="empty-state">Nenhum produto Auvo encontrado.</div>}{unifiedResults.auvoProducts?.map((record) => renderUnifiedRecord(record, 'external'))}</div>
            <div className="client-unified-block"><h3>Vínculos existentes</h3>{!unifiedResults.linkedRecords?.length && <div className="empty-state">Nenhum vínculo existente encontrado.</div>}{unifiedResults.linkedRecords?.map((record) => <button type="button" className="client-existing-link" key={record.id} onClick={() => openMasterDetail(record.linkedMasterProductId)}><strong>{formatSource(record.sourceSystem)} · {formatValue(record.name)}</strong><span>Mestre: {formatValue(record.linkedMasterProductName)}</span></button>)}</div>
          </div>
        )}
        {!!selectedExternalRecords.length && <div className="client-selected-actions mt-3"><div className="overlay-actions"><button type="button" className="btn btn-primary" disabled={!selectedMasterId || actionId === 'link-selected'} onClick={handleLinkSelected}>Vincular selecionados</button><button type="button" className="btn btn-outline-primary" disabled={actionId === 'create-master-selected'} onClick={handleCreateMasterWithSelected}>Criar Produto Mestre com selecionados</button></div></div>}
      </section>
      )}

      {detail && (
        <div className="client-detail-rail">
          <aside className="task-overlay-panel client-detail-drawer shadow-lg">
            <div className="overlay-panel-header">
              <div><div className="eyebrow">Auditoria</div><h2>{formatValue(detail.masterProduct?.name)}</h2><p className="text-muted mb-0">{formatValue(detail.masterProduct?.code || detail.masterProduct?.sku, 'Sem código')} · {detail.masterProduct?.status === 'active' ? 'Ativo' : 'Inativo'}</p></div>
              <div className="overlay-actions"><button type="button" className="btn btn-primary" disabled={!selectedAuditKeys.length || actionId === 'link-audit'} onClick={handleLinkAuditCandidates}>Vincular selecionados</button><button type="button" className="btn btn-outline-secondary" onClick={() => setDetail(null)}>Fechar</button></div>
            </div>
            <div className="client-detail-section mt-3">
              <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap"><h3>Produto Mestre</h3><button type="button" className="btn btn-sm btn-outline-primary" disabled={actionId === 'data-comparison'} onClick={handleOpenDataComparison}>Completar dados do Produto Mestre</button></div>
              <div className="client-readonly-grid">
                {COMPARISON_FIELDS.map(([field, label]) => <div key={field}><span>{label}</span><strong>{formatValue(detail.masterProduct?.[field])}</strong></div>)}
                <div><span>Origem principal</span><strong>{formatSource(detail.masterProduct?.primarySourceSystem)}</strong></div>
              </div>
            </div>
            {renderDataComparison()}
            {renderCandidateTable('Possíveis produtos do Bling', detail.blingCandidates || [])}
            {renderCandidateTable('Possíveis produtos do Auvo', detail.auvoCandidates || [])}
            <div className="client-detail-section mt-3">
              <h3>Produtos já vinculados</h3>
              <div className="client-linked-list">
                {!detail.linkedRecords?.length && <div className="empty-state">Nenhum produto vinculado.</div>}
                {detail.linkedRecords?.map((link) => <article key={link._id} className="client-linked-card"><div><strong>{formatSource(link.sourceSystem)} · {formatValue(link.externalName)}</strong><span>ID {formatValue(link.externalId)} · Código {formatValue(link.code)} · SKU {formatValue(link.sku)} · Barras {formatValue(link.barcode)}</span><span>Confiança {link.confidenceScore || 0}%</span></div></article>)}
              </div>
            </div>
          </aside>
        </div>
      )}
    </ShellTag>
  );
}
